import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BookOpen, Clock } from "lucide-react";
import type { Course } from "@/hooks/useCourses";
import { useStorageUrl } from "@/hooks/useStorageUrl";

interface CourseCardProps {
  course: Course;
}

export function CourseCard({ course }: CourseCardProps) {
  const thumbnailUrl = useStorageUrl("course-content", course.thumbnail_url);
  const instructorAvatarUrl = useStorageUrl("avatars", course.instructor?.avatar_url);
  return (
    <Link to={`/courses/${course.id}`}>
      <Card className="card-hover h-full flex flex-col overflow-hidden">
        <div className="aspect-video bg-muted relative">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={course.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20">
              <BookOpen className="h-12 w-12 text-muted-foreground" />
            </div>
          )}
          {course.is_enrolled && (
            <Badge className="absolute top-2 right-2 bg-accent text-accent-foreground">
              Enrolled
            </Badge>
          )}
        </div>

        <CardHeader className="pb-2">
          <h3 className="font-semibold text-lg line-clamp-2">{course.title}</h3>
        </CardHeader>

        <CardContent className="flex-1">
          {course.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {course.description}
            </p>
          )}

          {course.instructor && (
            <div className="flex items-center gap-2 mt-4">
              <Avatar className="h-6 w-6">
                <AvatarImage src={instructorAvatarUrl || undefined} />
                <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                  {course.instructor.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-muted-foreground">
                {course.instructor.full_name}
              </span>
            </div>
          )}
        </CardContent>

        <CardFooter className="pt-0 flex flex-col gap-2">
          <div className="flex items-center justify-between w-full text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <BookOpen className="h-4 w-4" />
              {course.modules_count || 0} modules
            </span>
          </div>

          {course.is_enrolled && course.progress !== undefined && (
            <div className="w-full">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Progress</span>
                <span>{course.progress}%</span>
              </div>
              <Progress value={course.progress} className="h-2" />
            </div>
          )}
        </CardFooter>
      </Card>
    </Link>
  );
}
